import React, { useState } from 'react';
import { TradeOrderDetailTableContext, ITradeOrderDetailTableContext } from './TradeOrderDetailTableContext';
import { useAsyncData } from '@shared/react';
import { usePagination2 } from '@features/Pagination/usePagination';
import { getLogo } from '@widgets/TransactionsTable/api';
import { getTradeOrder, getTradeOrderListing } from '@widgets/TradeOrderDetailTable/api';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

export const TradeOrderDetailTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [orderId, setOrderId] = useState<any>(null);
    const [dateRange, setDateRange] = useState<[string | null, string | null]>(['', '']);
    const [sortFieldName, setSortFieldName] = useState('id');

    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();
    let query: any = null;

    const {
        data: AssignPositionBody,
        handleRefresh: securityRefresh,
        loading: portfolioLoading,
    } = useAsyncData(
        async () => {
            if (orderId !== null) {
                if (typeof orderId === 'string' && orderId.includes('req')) {
                    // Remove 'req' from the orderId
                    const cleanOrderId = orderId.replace('req', '');

                    // Call the alternate request here
                    const { tradeOrders, totalCount } = await getTradeOrder(parseFloat(cleanOrderId));

                    setTotalCount(totalCount);
                    return tradeOrders || [];
                } else if (orderId !== 0 && !orderId.includes('req')) {
                    query = {
                        page,
                        pageSize,
                        sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                        filter: `jobId=${orderId}`,
                    };

                    const { items, totalResults } = await getTradeOrderListing(query);
                    setTotalCount(totalResults);
                    return items || [];
                }
            } else {
                return [];
            }
        },
        [],
        [pageSize, page, orderId],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            if (!AssignPositionBody) return [];
            return await Promise.all(
                AssignPositionBody.map(async (item: any) => {
                    const res = await getLogo({ symbol: item.symbol });
                    return { link: res, symbol: item.symbol };
                }),
            );
        },
        [],
        [AssignPositionBody],
    );

    const value: ITradeOrderDetailTableContext = {
        dateRange,
        AssignPositionBody: AssignPositionBody ?? [],
        paginationOptions,
        portfolioLoading,
        arrayWithLogos,
        sortFieldName,
        desc,
        setOrderId,
    };

    return <TradeOrderDetailTableContext.Provider value={value}>{children}</TradeOrderDetailTableContext.Provider>;
};
